/**
 * CardRemindersTab - one card or group's exception to its dashboard schedule.
 *
 * Cards and nested groups follow their dashboard by default, so this tab only
 * exists to say "not this one" or "this one has its own deadline". A group
 * override applies to every child that does not carry its own. The reminder
 * days themselves stay with the dashboard. Save is the panel's Save button.
 */
import { useEffect, useState } from 'react';
import Banner from 'src/components/ui/Banner';
import { InputField } from 'src/components/ui/Input';
import Loading from 'src/components/ui/Loading';
import { ToggleSetting } from 'src/components/ui/ToggleSetting';
import { toast } from 'src/components/ui/toast';
import { usePostHog } from 'src/lib/posthog';
import { useCardReminderOverride, useSetCardReminderOverride } from '../api/hooks';
import type { ReminderEditor } from './reminderEditor';

const MAX_DUE_OFFSET_DAYS = 60;

interface CardRemindersTabProps {
  cardId: string;
  /** Group copy talks about the children; a card talks about itself. */
  isGroup?: boolean;
  /** Only an owner may change it; everyone else sees what it is set to. */
  canEdit: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  onRegisterEditor?: (editor: ReminderEditor | null) => void;
  onOpenDashboardReminders?: () => void;
}

export default function CardRemindersTab({
  cardId,
  isGroup = false,
  canEdit,
  onDirtyChange,
  onRegisterEditor,
  onOpenDashboardReminders
}: CardRemindersTabProps) {
  const posthog = usePostHog();
  const { data: override, isLoading } = useCardReminderOverride(cardId);
  const saveOverride = useSetCardReminderOverride(cardId);

  const [excluded, setExcluded] = useState(false);
  const [ownDeadline, setOwnDeadline] = useState(false);
  const [dueOffset, setDueOffset] = useState(10);

  useEffect(() => {
    if (!override) return;
    setExcluded(override.excluded);
    setOwnDeadline(override.data_due_offset_days !== null);
    if (override.data_due_offset_days !== null) setDueOffset(override.data_due_offset_days);
  }, [override]);

  const dueOffsetValid = Number.isInteger(dueOffset) && dueOffset >= 0 && dueOffset <= MAX_DUE_OFFSET_DAYS;
  const savedDeadline = override?.data_due_offset_days ?? null;
  const dirty = !!override && (excluded !== override.excluded || (ownDeadline ? dueOffset : null) !== savedDeadline);

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!onRegisterEditor) return;
    onRegisterEditor({
      validate: () => {
        if (ownDeadline && !dueOffsetValid) {
          toast.error(`Deadline must be between 0 and ${MAX_DUE_OFFSET_DAYS} days.`);
          return false;
        }
        return true;
      },
      save: async () => {
        await saveOverride.mutateAsync({
          excluded,
          data_due_offset_days: ownDeadline ? dueOffset : null
        });
        posthog?.capture('dashboard_card_reminder_override_saved', {
          node_id: cardId,
          excluded,
          has_own_deadline: ownDeadline,
          is_group: isGroup
        });
      }
    });
    return () => onRegisterEditor(null);
  }, [onRegisterEditor, ownDeadline, dueOffsetValid, saveOverride, excluded, dueOffset, posthog, cardId, isGroup]);

  if (isLoading || !override) {
    return <Loading />;
  }

  return (
    <div className="space-y-5">
      <Banner variant="info">
        <span className="text-sm">
          {isGroup
            ? "This group follows its dashboard's reminder schedule. Change that here only if cards in the group need to."
            : "This card follows its dashboard's reminder schedule. Change that here only if it needs to."}{' '}
          {onOpenDashboardReminders && (
            <button type="button" className="text-misc-link hover:underline" onClick={onOpenDashboardReminders}>
              Open dashboard reminder settings
            </button>
          )}
        </span>
      </Banner>

      <ToggleSetting
        checked={excluded}
        onChange={setExcluded}
        disabled={!canEdit}
        title={isGroup ? 'Never remind about cards in this group' : 'Never remind about this card'}
        description={
          isGroup
            ? 'Use for a group nobody is expected to fill in every period. A child can still opt back in with its own deadline.'
            : 'Use for a card nobody is expected to fill in every period.'
        }
      />

      <div className={excluded ? 'opacity-50' : ''}>
        <ToggleSetting
          checked={ownDeadline}
          onChange={setOwnDeadline}
          disabled={!canEdit || excluded}
          title={isGroup ? "Give this group's cards their own deadline" : 'Give this card its own deadline'}
          description="For data that arrives later than the rest of the dashboard."
        />
        {ownDeadline && (
          <div className="flex items-center gap-2 mt-3">
            <InputField
              id="card-reminder-due-offset"
              type="number"
              min={0}
              max={MAX_DUE_OFFSET_DAYS}
              step={1}
              value={String(dueOffset)}
              disabled={!canEdit || excluded}
              state={dueOffsetValid ? 'default' : 'error'}
              onChange={(event) => setDueOffset(Number(event.target.value))}
              wrapperClassName="w-24"
              aria-label="Days after the period ends"
            />
            <span className="text-sm text-content-subtle">days after the period ends</span>
          </div>
        )}
      </div>
    </div>
  );
}
